import { Component, Input, OnDestroy, Renderer2 } from '@angular/core';
import { Calendar, Gift, LucideAngularModule, Star, Tag } from 'lucide-angular';
import { FormsModule } from '@angular/forms';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { RouterLink } from '@angular/router';
import { FeatureFlags } from '@app/config/feature-flags';

@Component({
  selector: 'app-product-item-tag-column',
  templateUrl: './product-item-tag-column.component.html',
  styleUrls: ['./product-item-tag-column.component.css'],
  imports: [LucideAngularModule, FormsModule, RouterLink],
})
export class ProductItemTagColumnComponent implements OnDestroy {
  @Input('item') protected item!: EventProductItemModel;
  protected readonly Tag = Tag;
  protected readonly Calendar = Calendar;
  protected readonly StarIcon = Star;
  protected readonly GiftIcon = Gift;

  private tooltipElement: HTMLElement | null = null;

  constructor(private renderer: Renderer2) {}

  get isVenueHire(): boolean {
    return (this.item?.product?.name || '').toLowerCase() === 'venue hire';
  }

  /**
   * TVG-159: Accommodation products carry a room variant per bedroom. These are
   * hidden on the Finance page so the single accommodation line and its price
   * still show without the long per-room list. The variants stay in the data
   * (they are attached to live events); the room detail lives in the
   * accommodation summary and on the website.
   */
  get isAccommodation(): boolean {
    return (
      this.item?.product?.triggers_accommodation_tab === true ||
      (this.item?.product?.category || '').toLowerCase() === 'accommodation'
    );
  }

  get displayDescription(): string | null {
    if (this.isVenueHire) {
      const venueName = this.item?.event_venue_name;
      if (venueName) {
        return `${venueName} Venue Hire`;
      }
      return 'Venue Hire';
    }

    const productDescription = this.item.product?.description;
    if (productDescription && productDescription !== 'null') {
      return productDescription;
    }

    return null;
  }

  getShortVariantName(selection: any): string {
    const variantName = selection.variant?.name || '';
    const optionName = selection.variant_option?.name || 'Standard';
    const fullName = `${variantName}: ${optionName}`;

    // Truncate if too long for name column
    const maxLength = 15;
    if (fullName.length > maxLength) {
      return fullName.substring(0, maxLength) + '...';
    }

    return fullName;
  }

  /**
   * Display-only: Sums backend-provided variant option prices for UI breakdown
   * NOTE: This is NOT calculating the product total price
   * Product total price comes from calculated_price field (single source of truth)
   * Uses effective_price from backend which includes daily pricing logic
   */
  getTotalAdditionalPrice(): number {
    if (!this.item.variant_selections || this.item.variant_selections.length === 0) {
      return 0;
    }

    return this.item.variant_selections.reduce((sum, selection) => {
      // Use effective_price if available (includes daily pricing), otherwise fall back to price
      const price = selection.variant_option?.['effective_price'] ?? selection.variant_option?.price ?? 0;
      const quantity = selection.quantity || 0;
      return sum + price * quantity;
    }, 0);
  }

  showTooltip(event: MouseEvent, content: string, isMultiple: boolean = false) {
    this.hideTooltip();

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    this.tooltipElement = this.renderer.createElement('div');

    // Basic styling
    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipElement, 'z-index', '999999');
    this.renderer.setStyle(this.tooltipElement, 'background-color', '#1f2937');
    this.renderer.setStyle(this.tooltipElement, 'color', 'white');
    this.renderer.setStyle(this.tooltipElement, 'font-size', '12px');
    this.renderer.setStyle(this.tooltipElement, 'padding', isMultiple ? '12px' : '8px');
    this.renderer.setStyle(this.tooltipElement, 'border-radius', '6px');
    this.renderer.setStyle(this.tooltipElement, 'box-shadow', '0 10px 15px -3px rgba(0, 0, 0, 0.1)');
    this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.tooltipElement, 'white-space', isMultiple ? 'normal' : 'nowrap');
    this.renderer.setStyle(this.tooltipElement, 'max-width', isMultiple ? '300px' : 'none');
    this.renderer.setStyle(this.tooltipElement, 'min-width', isMultiple ? '220px' : 'auto');

    // Position above the element
    const left = rect.left + rect.width / 2;
    const top = rect.top - 8;

    this.renderer.setStyle(this.tooltipElement, 'left', left + 'px');
    this.renderer.setStyle(this.tooltipElement, 'top', top + 'px');
    this.renderer.setStyle(this.tooltipElement, 'transform', 'translate(-50%, -100%)');

    // Set content
    if (this.tooltipElement) {
      this.tooltipElement.innerHTML = content;
    }

    // Append to body
    this.renderer.appendChild(document.body, this.tooltipElement);
  }

  hideTooltip() {
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }

  ngOnDestroy() {
    this.hideTooltip();
  }

  isOptionDiscontinued(option: any): boolean {
    // An option is discontinued if it has daily pricing capability but no price is set for this event date
    return option?.has_daily_prices === true && option?.is_using_daily_price === false;
  }

  private isPriceLocked(): boolean {
    return (
      FeatureFlags.PRICE_LOCKING &&
      this.item?.event_status === 'Confirmed' &&
      this.item?.amount !== null &&
      this.item?.amount !== undefined
    );
  }

  /**
   * Use locked unit price for confirmed events when available; otherwise fall back to live/effective price.
   * If the option price is missing (date-range pricing gap), fall back to the item's total for single-variant items.
   */
  getSelectionUnitPrice(selection: any): number {
    if (this.isPriceLocked() && selection?.locked_unit_price !== undefined && selection?.locked_unit_price !== null) {
      return selection.locked_unit_price;
    }
    const price = selection?.variant_option?.['effective_price'] ?? selection?.variant_option?.price ?? 0;

    const hasSingleSelection = (this.item?.variant_selections?.length ?? 0) === 1;
    if (
      price === 0 &&
      this.item?.product?.is_variant_based &&
      hasSingleSelection &&
      (this.item?.calculated_price ?? 0) > 0 &&
      (selection?.quantity ?? 0) > 0
    ) {
      return this.item.calculated_price! / selection.quantity;
    }

    return price;
  }
}
